import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

interface ResearchItem {
  id: string;
  title: string;
  description: string;
  status: string;
  year: string;
  category: string;
  createdAt: number;
}

interface TabConfig {
  id: string;
  name: string;
  type: 'status' | 'category';
}

const defaultStatusTabs: TabConfig[] = [
  { id: 'ongoing', name: '進行中', type: 'status' },
  { id: 'completed', name: '終了', type: 'status' },
  { id: 'planned', name: '計画中', type: 'status' }
];

const defaultCategoryTabs: TabConfig[] = [
  { id: 'cardiac-rehab', name: '心リハ', type: 'category' },
  { id: 'prevention', name: '循環器予防', type: 'category' },
  { id: 'exercise', name: '運動生理', type: 'category' },
  { id: 'remote-ai', name: '遠隔・AI・PHD', type: 'category' }
];

const Research = () => {
  const [researchItems, setResearchItems] = useState<ResearchItem[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [password, setPassword] = useState('');
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [statusTabs, setStatusTabs] = useState<TabConfig[]>(defaultStatusTabs);
  const [categoryTabs, setCategoryTabs] = useState<TabConfig[]>(defaultCategoryTabs);
  const [activeStatusTab, setActiveStatusTab] = useState('all');
  const [activeCategoryTab, setActiveCategoryTab] = useState('all');
  const [showTabManager, setShowTabManager] = useState(false);
  const [editingResearch, setEditingResearch] = useState<ResearchItem | null>(null);
  
  const [formData, setFormData] = useState({
    year: '',
    title: '',
    description: '',
    status: 'ongoing',
    category: 'cardiac-rehab'
  });

  const [newTabName, setNewTabName] = useState('');
  const [newTabType, setNewTabType] = useState<'status' | 'category'>('status');

  useEffect(() => {
    loadResearchItems();
    loadTabs();
  }, []);

  const loadResearchItems = () => {
    const stored = localStorage.getItem('researchItems');
    if (stored) {
      const parsed = JSON.parse(stored);
      setResearchItems(parsed.sort((a: ResearchItem, b: ResearchItem) => b.createdAt - a.createdAt));
    }
  };

  const loadTabs = () => {
    const storedStatusTabs = localStorage.getItem('researchStatusTabs');
    const storedCategoryTabs = localStorage.getItem('researchCategoryTabs');
    
    if (storedStatusTabs) {
      setStatusTabs(JSON.parse(storedStatusTabs));
    }
    if (storedCategoryTabs) {
      setCategoryTabs(JSON.parse(storedCategoryTabs));
    }
  };

  const handleAdminLogin = () => {
    if (password === 'cr.toranomon') {
      setIsAdmin(true);
      setPassword('');
    } else {
      alert('パスワードが正しくありません');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingResearch) {
      const updated = researchItems.map(item => 
        item.id === editingResearch.id 
          ? { ...item, ...formData }
          : item
      );
      localStorage.setItem('researchItems', JSON.stringify(updated));
      setResearchItems(updated);
      setEditingResearch(null);
      alert('研究テーマを更新しました');
    } else {
      const newItem: ResearchItem = {
        id: Date.now().toString(),
        title: formData.title,
        description: formData.description,
        status: formData.status,
        year: formData.year,
        category: formData.category,
        createdAt: Date.now()
      };

      const updated = [newItem, ...researchItems];
      localStorage.setItem('researchItems', JSON.stringify(updated));
      setResearchItems(updated);
      alert('研究テーマを登録しました');
    }
    
    setFormData({ year: '', title: '', description: '', status: 'ongoing', category: 'cardiac-rehab' });
  };

  const handleEdit = (item: ResearchItem) => {
    setEditingResearch(item);
    setFormData({
      year: item.year,
      title: item.title,
      description: item.description,
      status: item.status,
      category: item.category
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingResearch(null);
    setFormData({ year: '', title: '', description: '', status: 'ongoing', category: 'cardiac-rehab' });
  };

  const handleDelete = (id: string) => {
    if (window.confirm('この研究テーマを削除しますか？')) {
      const updated = researchItems.filter(item => item.id !== id);
      localStorage.setItem('researchItems', JSON.stringify(updated));
      setResearchItems(updated);
    }
  };

  const handleAddTab = () => {
    if (!newTabName.trim()) {
      alert('タブ名を入力してください');
      return;
    }

    const newTab: TabConfig = {
      id: `custom-${Date.now()}`,
      name: newTabName,
      type: newTabType
    };

    if (newTabType === 'status') {
      const updated = [...statusTabs, newTab];
      setStatusTabs(updated);
      localStorage.setItem('researchStatusTabs', JSON.stringify(updated));
    } else {
      const updated = [...categoryTabs, newTab];
      setCategoryTabs(updated);
      localStorage.setItem('researchCategoryTabs', JSON.stringify(updated));
    }

    setNewTabName('');
    alert('タブを追加しました');
  };

  const handleDeleteTab = (tabId: string, type: 'status' | 'category') => {
    if (!window.confirm('このタブを削除しますか？')) return;

    if (type === 'status') {
      const updated = statusTabs.filter(tab => tab.id !== tabId);
      setStatusTabs(updated);
      localStorage.setItem('researchStatusTabs', JSON.stringify(updated));
      if (activeStatusTab === tabId) setActiveStatusTab('all');
    } else {
      const updated = categoryTabs.filter(tab => tab.id !== tabId);
      setCategoryTabs(updated);
      localStorage.setItem('researchCategoryTabs', JSON.stringify(updated));
      if (activeCategoryTab === tabId) setActiveCategoryTab('all');
    }
  };

  const getStatusName = (statusId: string) => {
    const tab = statusTabs.find(t => t.id === statusId);
    return tab ? tab.name : statusId;
  };

  const getCategoryName = (categoryId: string) => {
    const tab = categoryTabs.find(t => t.id === categoryId);
    return tab ? tab.name : categoryId;
  };

  const filteredResearchItems = researchItems.filter(item => {
    const statusMatch = activeStatusTab === 'all' || item.status === activeStatusTab;
    const categoryMatch = activeCategoryTab === 'all' || item.category === activeCategoryTab;
    return statusMatch && categoryMatch;
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-24">
        {/* Hero */}
        <section className="py-20 bg-gradient-to-br from-primary-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              研究活動
            </h1>
            <p className="text-lg text-gray-700 max-w-3xl">
              虎の門病院循環器センター・心臓リハビリテーションチームでは、循環器予防学・運動生理学・心臓リハビリテーションを中心として数多くの研究を行っております。日本発のエビデンスの創出とエビデンスに基づいた医療の実践と発展に貢献できるよう日々邁進してまいります
            </p>
          </div>
        </section>

        {/* Research Overview */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">研究活動について</h2>
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    国家公務員共済組合連合会 虎の門病院 循環器センターでは、心疾患に関する様々な研究活動を行っています。
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    心臓リハビリテーションチームでは、高血圧や脂質異常などの循環器予防に関することや、運動耐容能などの運動生理学に関すること、心臓リハビリテーションに関することなど、多職種が連携して臨床研究に取り組み、得られたエビデンスを日常診療に還元することで、患者さんにより良い医療を提供することを目指しています。
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    研究成果は学会発表や論文として公表し、心不全診療の発展に貢献しています。
                  </p>
                </div>
              </div>
              <div className="w-full h-80 rounded-3xl overflow-hidden shadow-xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/574d6baaa4c2ec7e4c401cbf33810666.jpg"
                  alt="研究活動イメージ"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Admin Login */}
            {!isAdmin && (
              <div className="mb-8 text-right">
                <button
                  onClick={() => setShowAdminPanel(!showAdminPanel)}
                  className="text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  管理者ログイン
                </button>
              </div>
            )}

            {showAdminPanel && !isAdmin && (
              <div className="mb-12 bg-gray-50 rounded-lg p-6 max-w-md mx-auto">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">管理者ログイン</h3>
                <div className="flex gap-3">
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="パスワードを入力"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    onKeyPress={(e) => e.key === 'Enter' && handleAdminLogin()}
                  />
                  <button
                    onClick={handleAdminLogin}
                    className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap text-sm cursor-pointer"
                  >
                    ログイン
                  </button>
                </div>
              </div>
            )}

            {/* Admin Panel */}
            {isAdmin && (
              <div className="mb-12 bg-teal-50 rounded-lg p-8">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {editingResearch ? '研究テーマを編集' : '新しい研究テーマを登録'}
                  </h3>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowTabManager(!showTabManager)}
                      className="text-sm text-teal-600 hover:text-teal-800 cursor-pointer flex items-center gap-1"
                    >
                      <i className="ri-settings-3-line"></i>
                      タブ管理
                    </button>
                    <button
                      onClick={() => setIsAdmin(false)}
                      className="text-sm text-gray-600 hover:text-gray-800 cursor-pointer"
                    >
                      ログアウト
                    </button>
                  </div>
                </div>

                {showTabManager && (
                  <div className="mb-6 p-6 bg-white rounded-lg border border-teal-200">
                    <h4 className="font-semibold text-gray-900 mb-4">タブ管理</h4>
                    
                    <div className="mb-6">
                      <h5 className="text-sm font-medium text-gray-700 mb-3">状況タブ</h5>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {statusTabs.map(tab => (
                          <div key={tab.id} className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full">
                            <span className="text-sm">{tab.name}</span>
                            <button
                              onClick={() => handleDeleteTab(tab.id, 'status')}
                              className="text-red-500 hover:text-red-700 cursor-pointer"
                            >
                              <i className="ri-close-line text-sm"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mb-6">
                      <h5 className="text-sm font-medium text-gray-700 mb-3">テーマタブ</h5>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {categoryTabs.map(tab => (
                          <div key={tab.id} className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full">
                            <span className="text-sm">{tab.name}</span>
                            <button
                              onClick={() => handleDeleteTab(tab.id, 'category')}
                              className="text-red-500 hover:text-red-700 cursor-pointer"
                            >
                              <i className="ri-close-line text-sm"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <h5 className="text-sm font-medium text-gray-700 mb-3">新しいタブを追加</h5>
                      <div className="flex gap-3">
                        <select
                          value={newTabType}
                          onChange={(e) => setNewTabType(e.target.value as 'status' | 'category')}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        >
                          <option value="status">状況</option>
                          <option value="category">テーマ</option>
                        </select>
                        <input
                          type="text"
                          value={newTabName}
                          onChange={(e) => setNewTabName(e.target.value)}
                          placeholder="タブ名を入力"
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                        <button
                          onClick={handleAddTab}
                          className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap text-sm cursor-pointer"
                        >
                          追加
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        開始年度
                      </label>
                      <input
                        type="text"
                        value={formData.year}
                        onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                        placeholder="例：2024年〜"
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        状況
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      >
                        {statusTabs.map(tab => (
                          <option key={tab.id} value={tab.id}>{tab.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      テーマ
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    >
                      {categoryTabs.map(tab => (
                        <option key={tab.id} value={tab.id}>{tab.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      研究名
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="研究のタイトルを入力"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      内容説明
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="研究の内容を入力してください"
                      required
                      rows={6}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm resize-none"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
                    >
                      {editingResearch ? '更新する' : '登録する'}
                    </button>
                    {editingResearch && (
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors font-medium whitespace-nowrap cursor-pointer"
                      >
                        キャンセル
                      </button>
                    )}
                  </div>
                </form>
              </div>
            )}

            {/* Filter Tabs */}
            {researchItems.length > 0 && (
              <div className="mb-8">
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">状況で絞り込み</h3>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setActiveStatusTab('all')}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        activeStatusTab === 'all'
                          ? 'bg-teal-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      すべて
                    </button>
                    {statusTabs.map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveStatusTab(tab.id)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                          activeStatusTab === tab.id
                            ? 'bg-teal-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {tab.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">テーマで絞り込み</h3>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setActiveCategoryTab('all')}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        activeCategoryTab === 'all'
                          ? 'bg-teal-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      すべて
                    </button>
                    {categoryTabs.map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveCategoryTab(tab.id)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                          activeCategoryTab === tab.id
                            ? 'bg-teal-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {tab.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Research Items */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">主な研究テーマ</h2>
              {filteredResearchItems.length === 0 ? (
                <div className="text-center py-16">
                  <i className="ri-microscope-line text-6xl text-gray-300 mb-4"></i>
                  <p className="text-gray-500">
                    {researchItems.length === 0 ? 'まだ研究テーマはありません' : '該当する研究テーマがありません'}
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {filteredResearchItems.map((item) => (
                    <article
                      key={item.id}
                      className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-all duration-300"
                    >
                      <div className="flex items-start gap-6">
                        <div className="w-14 h-14 flex items-center justify-center bg-primary-100 rounded-xl flex-shrink-0">
                          <i className="ri-microscope-line text-2xl text-primary-600"></i>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                                item.status === 'ongoing' ? 'bg-green-100 text-green-700' : 
                                item.status === 'completed' ? 'bg-gray-100 text-gray-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {getStatusName(item.status)}
                              </span>
                              <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded-full whitespace-nowrap">
                                {getCategoryName(item.category)}
                              </span>
                              <span className="text-sm text-gray-500">{item.year}</span>
                            </div>
                            {isAdmin && (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEdit(item)}
                                  className="text-teal-600 hover:text-teal-700 cursor-pointer p-2"
                                >
                                  <i className="ri-edit-line text-xl"></i>
                                </button>
                                <button
                                  onClick={() => handleDelete(item.id)}
                                  className="text-red-500 hover:text-red-700 cursor-pointer p-2"
                                >
                                  <i className="ri-delete-bin-line text-xl"></i>
                                </button>
                              </div>
                            )}
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                          <p className="text-gray-600 leading-relaxed">{item.description}</p>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>

            {/* Publications */}
            <div className="bg-primary-50 rounded-3xl p-10 mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">学会発表・論文</h2>
              <p className="text-gray-600 text-center mb-8">
                研究成果は国内外の学会で発表し、論文として公表しています。詳細については、お問い合わせください。
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Link
                  to="/publications#conference"
                  className="bg-white p-6 rounded-2xl text-center hover:shadow-lg transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-xl mx-auto mb-4">
                    <i className="ri-presentation-line text-2xl text-primary-600"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">学会発表</h3>
                  <p className="text-gray-600 text-sm">日本循環器学会、日本心臓リハビリテーション学会等での発表</p>
                </Link>
                <Link
                  to="/publications#papers"
                  className="bg-white p-6 rounded-2xl text-center hover:shadow-lg transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-xl mx-auto mb-4">
                    <i className="ri-file-text-line text-2xl text-primary-600"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">論文発表</h3>
                  <p className="text-gray-600 text-sm">国内外の医学雑誌への論文投稿・掲載</p>
                </Link>
                <Link
                  to="/publications#collaborative"
                  className="bg-white p-6 rounded-2xl text-center hover:shadow-lg transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-xl mx-auto mb-4">
                    <i className="ri-team-line text-2xl text-primary-600"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">多施設共同研究</h3>
                  <p className="text-gray-600 text-sm">地域の医療機関との共同研究プロジェクト</p>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="bg-gradient-to-br from-teal-50 to-white p-10 rounded-2xl border border-teal-100">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-mail-line text-3xl text-white"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                研究に関するお問い合わせ
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                研究活動に関するご質問やご相談は、お気軽にお問い合わせください。
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-teal-500 text-white text-base font-medium rounded-lg hover:bg-teal-600 transition-all duration-300 hover:shadow-lg whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-line text-xl"></i>
                お問い合わせ
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Research;
